import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { Locale } from '@et/schema';
import { fetchTopicLessons, queryKeys, type ContentRev } from '../../lib/api.js';
import { localePath } from '../../lib/locale.js';
import { fetchLessonProgress, meKeys } from '../../lib/me.js';
import { useAccount } from '../account/account.js';
import { ProgressBar } from './ProgressBar.js';

/**
 * §49 — o mesmo progresso, visto do tópico.
 *
 * A lista vem de um join entre lições e tópicos, e a marca de concluído vem do
 * progresso de quem está a ler. Se ninguém tiver sessão, a secção continua a
 * aparecer: saber que existem lições sobre isto é útil antes de haver conta.
 */
export function TopicProgress({ locale, rev, slug }: {
  readonly locale: Locale; readonly rev: ContentRev; readonly slug: string;
}) {
  const { t } = useTranslation();
  const { user } = useAccount();

  const lessons = useQuery({
    queryKey: queryKeys.topicLessons(rev, locale, slug),
    queryFn: () => fetchTopicLessons(rev.version, locale, slug),
  });
  const progress = useQuery({
    queryKey: meKeys.lessonProgress, queryFn: fetchLessonProgress,
    enabled: user !== null, staleTime: 0,
  });

  const items = lessons.data?.items ?? [];
  if (items.length === 0) return null;

  const done = new Set((progress.data?.items ?? []).map((p) => p.lessonId));
  const completed = items.filter((l) => done.has(l.id)).length;

  return (
    <section className="et-claimpage__section et-topic__progress">
      <h2 className="et-claimpage__heading">{t('learn.title')}</h2>
      <ProgressBar
        value={Math.round((completed / items.length) * 100)}
        label={t('learn.lessonsDone', { n: completed, count: items.length })}
      />
      <ul className="et-course__lessons">
        {items.map((l) => (
          <li key={l.id} data-done={done.has(l.id) ? 'true' : undefined}>
            <Link to={localePath(locale, 'learn', l.courseId, 'lesson', l.id)}>{l.title}</Link>
            <span className="et-course__lesson-meta">{l.courseTitle}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
